"use client";

import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Input } from "@/components/ui/Form";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { Badge } from "@/components/ui/Badge/Badge";
import type { UserAdvert, AdvertPagination } from "@/lib/api/c-advertisements-user";
import styles from "./CAdvertisements.module.css";

/**
 * Prezentacyjna lista ogłoszeń usera - headless TanStack Table (parytet z
 * CProceduresView). Sortowanie klienckie w obrębie bieżącej strony; paginacja
 * server-side kontrolowana przez rodzica (CAdvertisementsView).
 */
export function AdvertList({
  adverts,
  pagination,
  page,
  loading,
  onPageChange,
  onNew,
  onEdit,
  onDelete,
}: {
  adverts: UserAdvert[];
  pagination: AdvertPagination | null;
  page: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onNew: () => void;
  onEdit: (a: UserAdvert) => void;
  onDelete: (a: UserAdvert) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [nameFilter, setNameFilter] = useState("");

  const fmtPrice = (a: UserAdvert) => {
    if (a.negotiated_price) return "do uzgodnienia";
    if (a.price === null) return "-";
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(a.price);
  };

  const columns = useMemo(() => {
    const col = createColumnHelper<UserAdvert>();
    return [
      col.display({
        id: "cover",
        header: () => <span className="sr-only">Miniatura</span>,
        cell: (ctx) => {
          const a = ctx.row.original;
          return (
            <button
              type="button"
              className={styles.thumb}
              onClick={() => onEdit(a)}
              aria-label={`Edytuj ogłoszenie: ${a.name}`}
            >
              {a.cover ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={a.cover.thumb_src}
                  alt={a.name}
                  width={a.cover.width ?? undefined}
                  height={a.cover.height ?? undefined}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <ImagePlaceholder />
              )}
            </button>
          );
        },
      }),
      col.accessor("name", {
        id: "name",
        header: () => "Nazwa",
        cell: (ctx) => {
          const a = ctx.row.original;
          return (
            <div className={styles.nameCell}>
              <span className={styles.advertName}>{a.name}</span>
              {a.availability_expired && <Badge variant="danger">Wygasło</Badge>}
            </div>
          );
        },
      }),
      col.accessor("category_name", {
        id: "category",
        header: () => "Kategoria",
        cell: (ctx) => ctx.getValue() || "-",
      }),
      col.accessor("price", {
        id: "price",
        header: () => "Cena",
        cell: (ctx) => <span className={styles.priceTag}>{fmtPrice(ctx.row.original)}</span>,
      }),
      col.accessor("availability", {
        id: "availability",
        header: () => "Ważne do",
        cell: (ctx) => ctx.getValue() || "-",
      }),
      col.accessor("public", {
        id: "public",
        header: () => "Status",
        cell: (ctx) => (
          <Badge variant={ctx.getValue() === 1 ? "success" : "neutral"}>
            {ctx.getValue() === 1 ? "Publikowane" : "Ukryte"}
          </Badge>
        ),
      }),
      col.display({
        id: "actions",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => {
          const a = ctx.row.original;
          return (
            <div className={styles.rowActions}>
              <button type="button" className={styles.btnLink} onClick={() => onEdit(a)}>
                Edytuj
              </button>
              <button type="button" className={styles.btnDanger} onClick={() => onDelete(a)}>
                Usuń
              </button>
            </div>
          );
        },
      }),
    ];
  }, [onEdit, onDelete]);

  /* Filtr klienta w obrębie bieżącej strony - backend `userAdverts` nie
     przyjmuje parametru wyszukiwania, więc szukanie działa tylko po tym
     co juz wczytane (aktualna strona paginacji). */
  const table = useReactTable({
    data: adverts,
    columns,
    state: { sorting, globalFilter: nameFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setNameFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = String(filterValue).trim().toLowerCase();
      if (!q) return true;
      return row.original.name.toLowerCase().includes(q);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredRows = table.getRowModel().rows;

  return (
    <div>
      <div className={styles.listToolbar}>
        <button type="button" className={styles.btnPrimary} onClick={onNew}>
          + Dodaj ogłoszenie
        </button>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj po nazwie..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            aria-label="Szukaj ogłoszeń po nazwie"
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.tableSkeleton} aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`skeleton-base ${styles.rowSkeleton}`} />
          ))}
        </div>
      ) : adverts.length === 0 ? (
        <p className={styles.empty}>Nie masz jeszcze żadnego ogłoszenia. Dodaj pierwsze!</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        className={canSort ? styles.thSortable : undefined}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        aria-sort={
                          sorted === "asc"
                            ? "ascending"
                            : sorted === "desc"
                              ? "descending"
                              : undefined
                        }
                      >
                        <span className={styles.thInner}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <Icon
                              name={sorted === "desc" ? "caret-down" : "caret-up"}
                              className={sorted ? styles.sortIconActive : styles.sortIcon}
                            />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={table.getAllLeafColumns().length} className={styles.emptyRow}>
                    Brak ogłoszeń pasujących do wyszukiwania na tej stronie.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.total_pages > 1 && (
        <nav className={styles.pagination} aria-label="Paginacja">
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            <Icon name="caret-left" />
            Poprzednia
          </button>
          <span className={styles.pageInfo}>
            Strona {pagination.page} z {pagination.total_pages} (łącznie {pagination.total})
          </span>
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(Math.min(pagination.total_pages, page + 1))}
            disabled={page >= pagination.total_pages}
          >
            Następna
            <Icon name="caret-right" />
          </button>
        </nav>
      )}
    </div>
  );
}
